import { Component } from "@angular/core";
import { forkJoin } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Department } from "@enums/department.enum";
import { OpsPlanningMode } from "@enums/opsPlanningMode.enum";
import { Shift } from "@enums/shift.enum";
import { environment } from "@environments/environment";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { HolidayRequestService } from "@services/holidayRequest.service";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeWorkOrderOperatorPriority } from "@interfaces/dto/changeWorkOrderOperatorPriority.interface";
import { ChangeWorkOrderStageEstimatedStartDate } from "@interfaces/dto/changeWorkOrderStageEstimatedStartDate.interface";
import { ChangeWorkOrderStageMachine } from "@interfaces/dto/changeWorkOrderStageMachine.interface";
import { ChangeWorkOrderStageOperator } from "@interfaces/dto/changeWorkOrderStageOperator.interface";
import { ChangeWorkOrderStageOperatorPriority } from "@interfaces/dto/changeWorkOrderStageOperatorPriority.interface";
import { WorkOrderStageIncrementQuantity } from "@interfaces/workOrderStageIncrementQuantity.interface";
import { Site } from "@interfaces/site.interface";
import { SiteService } from "@services/site.service";
import { UserProfileService } from "@services/userProfile.service";
import { WorkOrderService } from "@services/workOrder.service";
import { WorkOrderStageService } from "@services/workOrderStage.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "man-for-man",
	styleUrls: ["man-for-man.css"],
	templateUrl: "man-for-man.html"
})
export class OpsPlanningManForManAdminComponent {
	assignAvailableStages: any[] = [];
	assignSelectedPriority = 0;
	assignSelectedStageId: number | null = null;
	assignSelectedWorkOrderId: number | null = null;
	assignTargetOperatorId: number | null = null;
	collapsedDepartments: string[] = [];
	department: Department = Department.Assembly;
	departmentId = 0;
	departments: any;
	dragOverOperatorId: number | null = null;
	dragTargetStageId: number | null = null;
	draggedFromOperatorId: number | null = null;
	draggedStageId: number | null = null;
	groupedOperators: { department: string, operators: any[] }[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	holidayEndDate: string | number | Date = new Date();
	holidayOperatorId: number | null = null;
	holidayOperatorName = "";
	holidayStartDate: string | number | Date = new Date();
	incompleteWorkOrders: any[] = [];
	isLoading = true;
	machines: any[] = [];
	minDate: Date = new Date();
	operatorSearchTerm = "";
	operators: any[] = [];
	operatorsShowingAll: number[] = [];
	opsPlanningDate: Date = new Date();
	opsPlanningMode: OpsPlanningMode = OpsPlanningMode.ForDate;
	opsPlanningModes: any;
	popupAddButtonOptions: any;
	popupChangeWorkOrderStageMachine: ChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupIncrementQuantityData: WorkOrderStageIncrementQuantity = new WorkOrderStageIncrementQuantity();
	popupTitle = "";
	popupVisible = false;
	priorities: number[];
	selectedSiteId: number | null = null;
	selectedSupervisorId: number | null = null;
	shifts: any;
	showAssignModal = false;
	showHolidayModal = false;
	sites: Site[] = [];
	supervisors: any[] = [];
	title = "Operator Plan";

	constructor(
		private holidayRequestService: HolidayRequestService,
		private router: Router,
		private toastService: ToastService,
		private route: ActivatedRoute,
		private siteService: SiteService,
		private userProfileService: UserProfileService,
		private workOrderService: WorkOrderService,
		private workOrderStageService: WorkOrderStageService
	) {
		this.departments = GetPropertiesFromEnum(Department);
		this.opsPlanningModes = GetPropertiesFromEnum(OpsPlanningMode);
		this.shifts = GetPropertiesFromEnum(Shift);
		this.getOperators();
		this.getSites();
		this.getSupervisors();
		this.getIncompleteWorkOrders();
		this.changeOpsPlanningDate = this.changeOpsPlanningDate.bind(this);
		this.changeOpsPlanningMode = this.changeOpsPlanningMode.bind(this);
		this.viewToday = this.viewToday.bind(this);

		this.priorities = [];
		for (let i = 0; i <= environment.build.lowestWorkOrderStagePriority; i++) {
			this.priorities.push(i);
		}
	}

	buildButtons() {
		this.headerTertiaryButtons = [
			{ icon: "fal fa-sync-alt", method: "getOperators", text: "" },
			{ method: "goToFirewall", text: "Firewall" },
			{ method: "", style: "background-color: #10B981; color: white;", text: "Operator Plan" },
			{ method: "goToWorkforce", text: "Workforce" },
			{ method: "goToOutsideProcessMovements", text: "OPM" }
		];

		this.headerSecondaryButtons = [];

		const filteredDepartments = this.departments.filter((d: { id: number; name: string; }) => (d.id > 0 && d.id < 55) || d.id == 70);
		filteredDepartments.forEach((fd: any) => {
			this.headerSecondaryButtons.push({ method: "goToDepartment-" + fd.id, text: fd.name });
		});

		this.headerPrimaryButtons = [];

		this.sites.forEach((site: any) => {
			this.headerPrimaryButtons.push({ method: "goToSite-" + site.id, text: site.name });
		});

		this.title = "Operator Plan";
	}

	buildGroupedOperators() {
		let filtered = this.selectedSiteId
			? this.operators.filter((op: any) => op.siteId === this.selectedSiteId)
			: this.operators;
		if (this.selectedSupervisorId) {
			filtered = filtered.filter((op: any) => op.supervisorId === this.selectedSupervisorId);
		}
		if (this.operatorSearchTerm && this.operatorSearchTerm.trim() !== "") {
			const term = this.operatorSearchTerm.trim().toLowerCase();
			filtered = filtered.filter((op: any) =>
				`${op.firstName} ${op.lastName}`.toLowerCase().includes(term)
			);
		}
		const groups: { [key: string]: any[] } = {};
		for (const op of filtered) {
			const dept = op.currentDepartmentName || "Unknown";
			if (!groups[dept]) {
				groups[dept] = [];
			}
			groups[dept].push(op);
		}
		this.groupedOperators = Object.keys(groups).map(dept => ({ department: dept, operators: groups[dept] }));
	}

	changeOperatorPriority(workOrderId: number, priority: number) {
		this.isLoading = true;
		const data = new ChangeWorkOrderOperatorPriority();
		data.operatorPriority = priority;
		data.workOrderId = workOrderId;
		this.workOrderService.changeOperatorPriority(data)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Operator Priority Changed");
					this.getOperators();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	changeOperatorPriorityDropDown(args: any, workOrderId: number) {
		const newPriority = args.value;
		this.changeOperatorPriority(workOrderId, newPriority);
	}

	changeOpsPlanningDate(e: any) {
		this.opsPlanningDate = e.value;
		this.getOperators();
	}

	changeOpsPlanningMode(e: any) {
		this.opsPlanningMode = e.value;
		this.getOperators();
	}

	changeSiteFilter(e: any) {
		this.selectedSiteId = e.value;
		this.buildGroupedOperators();
	}

	changeStagePriorityDropDown(args: any, stageId: number) {
		this.isLoading = true;
		const data = new ChangeWorkOrderStageOperatorPriority();
		data.workOrderStageId = stageId;
		data.operatorPriority = args.value;
		this.workOrderStageService.changeOperatorPriority(data)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Stage Priority Changed");
					this.getOperators();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeSupervisorFilter(e: any) {
		this.selectedSupervisorId = e.value;
		this.buildGroupedOperators();
	}

	changeWorkOrderStageMachine() {
		this.isLoading = true;
		this.workOrderStageService.changeMachine(this.popupChangeWorkOrderStageMachine)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Machine Updated");
					this.getOperators();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageMachinePopup(stageId: number) {
		this.popupChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
		this.popupChangeWorkOrderStageMachine.workOrderStageId = stageId;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageMachine(), text: "Change Machine" };
		this.popupTitle = "Change Machine";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageOperator(stageId: number, operatorId: number) {
		this.isLoading = true;
		const data = new ChangeWorkOrderStageOperator();
		data.workOrderStageId = stageId;
		data.userProfileId = operatorId;
		data.operatorQueue = "[]";
		this.workOrderStageService.changeOperator(data)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage reassigned to operator");
					this.getOperators();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	closeAssignModal() {
		this.showAssignModal = false;
		this.assignTargetOperatorId = null;
		this.assignSelectedWorkOrderId = null;
		this.assignSelectedStageId = null;
		this.assignSelectedPriority = 0;
		this.assignAvailableStages = [];
	}

	closeHolidayModal() {
		this.showHolidayModal = false;
		this.holidayOperatorId = null;
		this.holidayOperatorName = "";
		this.holidayStartDate = new Date();
		this.holidayEndDate = new Date();
	}

	closePopup() {
		this.popupVisible = false;
	}

	collapseAll() {
		this.collapsedDepartments = this.groupedOperators.map((g: any) => g.department);
	}

	displayMachineInDropdown(e: any) {
		return e === null ? null : e.cellNumber === null ? e.machineName : `${e.cellNumber} - ${e.machineName}`;
	}

	displayStageInDropdown(e: any) {
		if (e === null) return null;
		let text = `OP${e.stageNumber}0 - ${e.process?.name || "Unknown"}`;
		if (e.machine?.cellNumber) text += ` (${e.machine.cellNumber})`;
		return text;
	}

	displayWorkOrderInDropdown(e: any) {
		return e === null ? null : `${e.id} - ${e.part?.partNumber || ""}`;
	}

	expandAll() {
		this.collapsedDepartments = [];
	}

	generateAndDownloadWorkOrderMasterLabelPDF(workOrderId: number) {
		this.workOrderService.generateAndDownloadMasterLabelPdf(workOrderId).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Work_Order_Master_Label_" + workOrderId + ".pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			},
		);
	}

	getIncompleteWorkOrders() {
		this.workOrderService.getAllIncomplete()
			.subscribe(
				(res: any) => {
					this.incompleteWorkOrders = res.response;
				},
				(err) => {
					console.log(err);
				}
			);
	}

	getOperatorAvatar(operator: any) {
		if (operator.profileImage) return operator.profileImage;
		const first = operator.firstName ? operator.firstName.substring(0, 1) : "?";
		const last = operator.lastName ? operator.lastName.substring(0, 1) : "?";
		return `https://ui-avatars.com/api/?name=${first}+${last}&size=64&background=344281&color=ffffff`;
	}

	getOperators() {
		this.isLoading = true;
		let request;
		switch (this.opsPlanningMode) {
			case OpsPlanningMode.All:
				request = this.userProfileService.manForMan();
				break;
			case OpsPlanningMode.ForDate:
				request = this.userProfileService.manForManForDate(this.opsPlanningDate);
				break;
			case OpsPlanningMode.Unestimated:
				request = this.userProfileService.manForManUnestimated();
				break;
		}
		request.subscribe(
			(res: any) => {
				this.operators = res.response;
				this.buildGroupedOperators();
				this.isLoading = false;
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	getSites() {
		this.isLoading = true;
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.buildButtons();
					this.isLoading = false;
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getSupervisors() {
		this.userProfileService.getSupervisors()
			.subscribe(
				(res: any) => { this.supervisors = res.response || []; },
				(err) => { console.log(err); }
			);
	}

	goToDepartment(department: number) {
		this.router.navigate([`/admin/opsPlanning/department/${department}`]);
	}

	goToFirewall() {
		this.router.navigate(["/admin/opsPlanning/firewall"]);
	}

	goToOutsideProcessMovements() {
		this.router.navigate(["/admin/opsPlanning/outsideProcessContractMovements"]);
	}

	goToSite(site: number) {
		this.router.navigate([`/admin/opsPlanning/site/${site}`]);
	}

	goToWorkforce() {
		this.router.navigate(["/admin/opsPlanning/workforce"]);
	}

	headerButtonClick(method: string) {
		if (method.indexOf("-") > 0) {
			const split = method.split("-");
			// @ts-ignore
			this[split[0]](split[1]);
		}
		else {
			// @ts-ignore
			if (this[method]) this[method]();
		}
	}

	incrementWorkOrderStage() {
		if (this.popupIncrementQuantityData.quantity > this.popupIncrementQuantityData.maximumQuantity) {
			alert("Quantity too high");
			return;
		}
		this.workOrderStageService.completedQuantity(this.popupIncrementQuantityData.id, this.popupIncrementQuantityData.quantity)
			.subscribe(
				() => {
					this.toastService.success("Completed Quantity Incremented");
					this.getOperators();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	incrementWorkOrderStagePopup(stageId: number, quantityCompleted: number, quantityRequired: number) {
		this.popupIncrementQuantityData = new WorkOrderStageIncrementQuantity();
		this.popupIncrementQuantityData.id = stageId;
		this.popupIncrementQuantityData.maximumQuantity = quantityRequired - quantityCompleted;
		this.popupIncrementQuantityData.quantity = 1;
		this.popupAddButtonOptions = { onClick: () => this.incrementWorkOrderStage(), text: "Increment Completed Quantity" };
		this.popupTitle = "Increment Completed Quantity";
		this.popupVisible = true;
	}

	markSickness(operatorId: number) {
		this.isLoading = true;
		const today = this.opsPlanningDate || new Date();
		this.holidayRequestService.createApproved({
			endDate: today,
			isSickness: true,
			startDate: today,
			userProfileId: operatorId
		}).subscribe(
			() => {
				this.isLoading = false;
				this.toastService.success("Sickness recorded");
				this.getOperators();
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	onDragEnd() {
		this.draggedStageId = null;
		this.draggedFromOperatorId = null;
		this.dragOverOperatorId = null;
		this.dragTargetStageId = null;
	}

	onDragLeave(operatorId: number) {
		if (this.dragOverOperatorId === operatorId) {
			this.dragOverOperatorId = null;
		}
	}

	onDragLeaveStage(stageId: number) {
		if (this.dragTargetStageId === stageId) {
			this.dragTargetStageId = null;
		}
	}

	onDragOver(event: DragEvent, operatorId: number) {
		event.preventDefault();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverOperatorId = operatorId;
	}

	onDragOverStage(event: DragEvent, stageId: number, operatorId: number) {
		event.preventDefault();
		event.stopPropagation();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverOperatorId = operatorId;
		if (this.draggedFromOperatorId === operatorId && stageId !== this.draggedStageId) {
			this.dragTargetStageId = stageId;
		}
	}

	onDragStart(event: DragEvent, stageId: number, operatorId: number) {
		this.draggedStageId = stageId;
		this.draggedFromOperatorId = operatorId;
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
		}
	}

	onDropOnOperator(targetOperatorId: number) {
		if (this.draggedStageId === null || this.draggedFromOperatorId === targetOperatorId) {
			this.onDragEnd();
			return;
		}
		this.changeWorkOrderStageOperator(this.draggedStageId, targetOperatorId);
		this.onDragEnd();
	}

	onDropOnStage(event: DragEvent, targetStageId: number, targetOperatorId: number) {
		event.stopPropagation();
		if (this.draggedStageId === null || this.draggedStageId === targetStageId) {
			this.onDragEnd();
			return;
		}
		if (this.draggedFromOperatorId === targetOperatorId) {
			this.reorderWithinOperator(targetOperatorId, this.draggedStageId, targetStageId);
		} else {
			this.onDropOnOperator(targetOperatorId);
		}
	}

	onWorkOrderSelected(e: any) {
		this.assignSelectedStageId = null;
		if (!e.value) {
			this.assignAvailableStages = [];
			return;
		}
		this.assignAvailableStages = [];
		this.workOrderStageService.getForWorkOrder(e.value)
			.subscribe(
				(res: any) => {
					this.assignAvailableStages = (res.response || []).filter((s: any) => s.stageComplete === false);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Could not load stages.");
				}
			);
	}

	openAssignModal(operatorId: number) {
		this.assignTargetOperatorId = operatorId;
		this.assignSelectedWorkOrderId = null;
		this.assignSelectedStageId = null;
		this.assignAvailableStages = [];
		this.showAssignModal = true;
	}

	openHolidayModal(operatorId: number, firstName: string, lastName: string) {
		this.holidayOperatorId = operatorId;
		this.holidayOperatorName = `${firstName} ${lastName}`;
		this.holidayStartDate = new Date();
		this.holidayEndDate = new Date();
		this.showHolidayModal = true;
	}

	printManForMan() {
		const date = this.opsPlanningDate ? new Date(this.opsPlanningDate).toLocaleDateString("en-GB") : "All";
		const selectedSite = this.selectedSiteId ? this.sites.find((s: any) => s.id === this.selectedSiteId) : null;
		const siteLabel = selectedSite ? ` - ${selectedSite.name}` : "";
		const selectedSupervisor = this.selectedSupervisorId ? this.supervisors.find((s: any) => s.id === this.selectedSupervisorId) : null;
		const supervisorLabel = selectedSupervisor ? ` - Supervisor: ${selectedSupervisor.name}` : "";
		let content = "";
		for (const group of this.groupedOperators) {
			content += `<div class="dept">${group.department}</div>`;
			for (const op of group.operators) {
				const nameColor = op.isOnSickness ? "#dc2626" : op.isOnHoliday ? "#2563eb" : op.isClockedIn ? "#059669" : "#6b7280";
				const absentClass = op.isOnHoliday ? " absent" : "";
				const allStages = [...(op.runningStages || []), ...(op.availableStages || [])];
				let stagesHtml = "";
				if (allStages.length > 0) {
					stagesHtml = allStages.map((s: any, i: number) =>
						`<div class="card${s.stageStarted && !s.isPaused ? " running" : ""}">
							<div class="card-num">${i + 1}</div>
							<div class="card-body">
								<div><strong>${s.workOrderId}</strong></div>
								<div>${s.partNumber}</div>
								<div>${s.machineCellNumber ? "<strong>" + s.machineCellNumber + "</strong> | " : ""}${s.operationNumber}</div>
								<div>${s.quantityCompleted}/${s.quantityRequired}</div>
							</div>
						</div>`
					).join("");
				} else {
					stagesHtml = "<div class=\"empty\">No work assigned</div>";
				}
				const icon = op.isOnSickness ? "<i class=\"fa fa-medkit\"></i> " : op.isOnHoliday ? "<i class=\"fa fa-umbrella-beach\"></i> " : "";
				content += `<div class="op-row${absentClass}">
					<div class="op-name" style="color:${nameColor}">${icon}<strong>${op.firstName} ${op.lastName}</strong></div>
					<div class="stages">${stagesHtml}</div>
				</div>`;
			}
		}
		const html = `<!DOCTYPE html><html><head><title>Operator Plan - ${date}</title>
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css">
			<style>
			body{font-family:Arial,sans-serif;padding:20px;color:#111;font-size:11px;}
			h1{font-size:16px;margin-bottom:4px;} h2{font-size:12px;color:#666;margin-top:0;}
			.dept{background:#e5e7eb;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;padding:6px 8px;border-radius:3px;margin:10px 0 4px 0;}
			.op-row{display:flex;align-items:stretch;border:1px solid #e5e7eb;border-radius:4px;margin-bottom:4px;min-height:40px;}
			.op-row.absent{opacity:0.5;}
			.op-name{width:140px;min-width:140px;padding:6px 8px;display:flex;align-items:center;gap:6px;border-right:1px solid #e5e7eb;font-size:10px;}
			.stages{display:flex;align-items:stretch;gap:4px;padding:4px;flex-wrap:wrap;flex:1;}
			.card{display:flex;border:1px solid #e5e7eb;border-left:3px solid orange;border-radius:3px;font-size:9px;width:140px;min-width:140px;}
			.card.running{border-left:3px solid #10B981;background:#f0fdf4;}
			.card-num{padding:4px 6px;color:#9ca3af;font-weight:600;display:flex;align-items:center;}
			.card-body{padding:4px 6px;}
			.card-body div{margin-bottom:1px;}
			.empty{color:#9ca3af;font-style:italic;padding:6px 8px;display:flex;align-items:center;}
			* {-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
			@media print{body{padding:0;} @page{size:landscape;margin:10mm;}}
		</style></head><body>
			<h1>Operator Plan${siteLabel}${supervisorLabel}</h1><h2>${date}</h2>
			${content}
		</body></html>`;
		const win = window.open("", "_blank");
		if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
	}

	recalculateStageEstimates(stageId: number) {
		this.isLoading = true;
		this.workOrderStageService.recalculateEstimates(stageId)
			.subscribe(
				() => {
					this.isLoading = false;
					this.getOperators();
					this.toastService.success("Estimates Reworked, reloading details.");
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		return false;
	}

	renderDateString(date: Date) {
		const dateToChange = new Date(date);
		return dateToChange.toLocaleDateString("en-GB");
	}

	reorderWithinOperator(operatorId: number, draggedId: number, targetId: number) {
		const operator = this.operators.find((o: any) => o.userProfileId === operatorId);
		if (!operator) { this.onDragEnd(); return; }

		const stages = [...operator.availableStages].sort((a: any, b: any) => a.operatorPriority - b.operatorPriority);
		const fromIndex = stages.findIndex((s: any) => s.id === draggedId);
		const toIndex = stages.findIndex((s: any) => s.id === targetId);

		if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
			this.onDragEnd();
			return;
		}

		const [moved] = stages.splice(fromIndex, 1);
		stages.splice(toIndex, 0, moved);

		const updates = stages
			.map((stage: any, index: number) => ({ newPriority: index + 1, stage }))
			.filter(({ newPriority, stage }: { newPriority: number, stage: any }) => stage.operatorPriority !== newPriority)
			.map(({ newPriority, stage }: { newPriority: number, stage: any }) => {
				const data = new ChangeWorkOrderStageOperatorPriority();
				data.workOrderStageId = stage.id;
				data.operatorPriority = newPriority;
				return this.workOrderStageService.changeOperatorPriority(data);
			});

		if (updates.length === 0) {
			this.onDragEnd();
			return;
		}

		this.isLoading = true;
		forkJoin(updates).subscribe(
			() => {
				this.isLoading = false;
				this.toastService.success("Operator queue order updated");
				this.getOperators();
				this.onDragEnd();
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
				this.onDragEnd();
			}
		);
	}

	showRunningSections() {
		return this.opsPlanningMode === OpsPlanningMode.ForDate && this.opsPlanningDate.toDateString() === this.minDate.toDateString();
	}

	submitAssignModal() {
		if (!this.assignSelectedStageId || !this.assignTargetOperatorId) return;
		this.isLoading = true;
		const operatorData = new ChangeWorkOrderStageOperator();
		operatorData.workOrderStageId = this.assignSelectedStageId;
		operatorData.userProfileId = this.assignTargetOperatorId;
		operatorData.operatorQueue = "[]";

		const onSuccess = () => {
			this.isLoading = false;
			this.toastService.success("Stage assigned to operator");
			this.closeAssignModal();
			this.getOperators();
		};

		const onError = (err: any) => {
			this.isLoading = false;
			console.log(err);
			this.toastService.error("Something Went Wrong!");
		};

		const priorityData = new ChangeWorkOrderStageOperatorPriority();
		priorityData.workOrderStageId = this.assignSelectedStageId!;
		priorityData.operatorPriority = this.assignSelectedPriority;

		this.workOrderStageService.changeOperator(operatorData)
			.subscribe(
				() => {
					const followUps = [this.workOrderStageService.changeOperatorPriority(priorityData)];

					if (this.opsPlanningMode === OpsPlanningMode.ForDate) {
						const dateData = new ChangeWorkOrderStageEstimatedStartDate();
						dateData.workOrderStageId = this.assignSelectedStageId!;
						dateData.estimatedStartDate = this.opsPlanningDate;
						followUps.push(this.workOrderStageService.changeEstimatedStartDate(dateData));
					}

					forkJoin(followUps).subscribe(onSuccess, onError);
				},
				onError
			);
	}

	submitHolidayModal() {
		if (!this.holidayOperatorId || !this.holidayStartDate || !this.holidayEndDate) return;
		this.isLoading = true;
		this.holidayRequestService.createApproved({
			endDate: this.holidayEndDate,
			startDate: this.holidayStartDate,
			userProfileId: this.holidayOperatorId
		}).subscribe(
			() => {
				this.isLoading = false;
				this.toastService.success("Holiday booked");
				this.closeHolidayModal();
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	toggleDepartmentCollapse(department: string) {
		const idx = this.collapsedDepartments.indexOf(department);
		if (idx >= 0) {
			this.collapsedDepartments.splice(idx, 1);
		} else {
			this.collapsedDepartments.push(department);
		}
	}

	toggleOperatorShowingAll(operatorId: number) {
		const currentIndex = this.operatorsShowingAll.indexOf(operatorId);
		if (currentIndex >= 0) {
			this.operatorsShowingAll.splice(currentIndex, 1);
		} else {
			this.operatorsShowingAll.push(operatorId);
		}
		return false;
	}

	toggleStagePause(workOrderStageId: number) {
		this.workOrderStageService.togglePause(workOrderStageId)
			.subscribe(
				() => {
					this.toastService.success("Successfully Toggled Pause");
					this.getOperators();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		return false;
	}

	unassignStage(workOrderStageId: number) {
		this.isLoading = true;
		const data = new ChangeWorkOrderStageOperator();
		data.workOrderStageId = workOrderStageId;
		data.userProfileId = 0;
		data.operatorQueue = "[]";
		this.workOrderStageService.changeOperator(data)
			.subscribe(
				() => {
					this.toastService.success("Stage unassigned");
					this.getOperators();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	viewToday() {
		this.opsPlanningDate = new Date();
		this.opsPlanningMode = OpsPlanningMode.ForDate;
	}
}
